import { useEffect, useRef, useState } from "react";
import { lei } from "@/shared/format";
import { Menu } from "./Menu";
import { Checkout } from "./Checkout";
import { OrderStatus } from "./OrderStatus";
import { Rent } from "./Rent";
import { useCart } from "./cart";
import {
  alertAlreadyFired, announceReady, markAlertFired, markReadyDismissed,
  primeChimeOnFirstGesture, readyDismissed,
} from "./ready-alert";

export type HouseRules = {
  payment_methods: string[];
  /**
   * What each method REQUIRES of the guest (v8): `email: true` on cash means
   * the checkout must collect an address, `phone: true` on any method means a
   * number. Absent on a pre-v8 snapshot — read as the defaults (online email
   * only), which is what every pre-v8 evening already did.
   */
  checkout_contact?: Record<string, { email: boolean; phone: boolean }>;
  tips_enabled: boolean;
  tip_percents: number[];
  require_guest_name: boolean;
  closing_grace_hours: number;
  /**
   * The house's own terms and conditions.
   *
   * Absent on a pre-v5 snapshot and "" for a house that has published none —
   * and both mean the same thing to the checkout, which is that it draws NO
   * acceptance box. A tick box linking nowhere asks the guest to agree to a
   * document that does not exist.
   */
  terms_url?: string;
};

/**
 * Routing without a router.
 *
 * Four shapes only:
 *   /m/<event-slug>/<zone>   the QR on the table
 *   /o/<order-id>            the order the guest just placed
 *   /?e=<slug>&z=<zone>      the same as the first, for a typed link
 *   /r , /r/<unit-slug>      the selling page — what the house rents
 *
 * A router library would be ~10 KB of the guest's 3G budget to resolve four
 * routes, on a page that must render in under two seconds.
 *
 * `?bar=1` IS NOT A FOURTH SHAPE. It is the same menu, same slug, same cart,
 * opened by somebody standing behind the counter typing for a guest who cannot
 * scan a QR — see MOD BAR below. It carries no authority of its own: the
 * server reads the staff cookie or refuses, and a guest who types it by hand
 * gets a menu that asks them to sign in.
 */
function route():
  | { kind: "menu"; slug: string | null; zone: string | null; bar: boolean }
  | { kind: "order"; id: string }
  | { kind: "rent"; slug: string | null } {
  const url = new URL(location.href);
  const parts = url.pathname.split("/").filter(Boolean);
  const bar = url.searchParams.get("bar") === "1";

  if (parts[0] === "o" && parts[1]) return { kind: "order", id: parts[1] };
  /*
   * /r            the units this house rents
   * /r/<slug>     one of them, with its own page
   *
   * A FOURTH SHAPE, and a real one rather than a mode of the menu: it sells a
   * different thing, on a different day, to somebody who is not in the house
   * yet. `/r/*` is in `assets.run_worker_first` so the Worker gets to brand the
   * shell before this ever runs (D-23, wrangler.jsonc).
   */
  if (parts[0] === "r") return { kind: "rent", slug: parts[1] ? decodeURIComponent(parts[1]) : null };
  if (parts[0] === "m" && parts[1]) {
    return { kind: "menu", slug: parts[1], zone: parts[2] ?? url.searchParams.get("z"), bar };
  }
  return { kind: "menu", slug: url.searchParams.get("e"), zone: url.searchParams.get("z"), bar };
}

/**
 * ============================================================================
 * MOD BAR — WHO IS TYPING, AND MAY THEY.
 * ============================================================================
 *
 * Some guests do not scan anything. They are eighty, the QR card is a grey
 * square, and they walk to the counter and say what they want. So staff open
 * the SAME menu with `?bar=1`, build the basket, and place the order with „la
 * bar, în numerar" already chosen and nothing asked of the guest.
 *
 * THE CREDENTIAL IS THE ONE THEY ALREADY HAVE. /staff and this page are the
 * same origin, so the session or device cookie is simply already in the jar —
 * there is no token in the link, nothing to hand out and nothing to leak, and
 * revoking a tablet already reaches this. `/api/auth/me` is only the SCREEN's
 * copy of the answer, so it can say who is signed in and grey the bar when
 * nobody is; worker/api/orders.ts asks the question again for real.
 *
 * `null` while unknown, and the checkout treats unknown as "not yet" for the
 * same reason it treats unknown house rules that way.
 */
export type BarStaff = { name: string; canOrder: boolean };

const BAR_COPY = {
  ro: {
    who: "Comandă la bar",
    signIn: "Autentifică-te ca personal",
    signInHint: "Deschide /staff pe acest dispozitiv, apoi revino.",
    denied: "Acest cont nu poate lua comenzi la bar.",
    placed: "Comandă înregistrată",
    collect: "De încasat",
    code: "Cod",
    next: "Comandă nouă",
    settle: "Marchează „Am încasat” pe ecranul barului după ce iei banii.",
  },
  ru: {
    who: "Заказ у бара",
    signIn: "Войдите как персонал",
    signInHint: "Откройте /staff на этом устройстве и вернитесь.",
    denied: "Этот аккаунт не может принимать заказы у бара.",
    placed: "Заказ принят",
    collect: "К оплате",
    code: "Код",
    next: "Новый заказ",
    settle: "Отметьте «Получено» на экране бара после получения денег.",
  },
  en: {
    who: "Order at the bar",
    signIn: "Sign in as staff",
    signInHint: "Open /staff on this device, then come back.",
    denied: "This account cannot take orders at the bar.",
    placed: "Order placed",
    collect: "To collect",
    code: "Code",
    next: "New order",
    settle: "Tap „Am încasat” on the bar screen once you have the cash.",
  },
} as const;

const barCopy = (lang: string) => BAR_COPY[(lang in BAR_COPY ? lang : "ro") as keyof typeof BAR_COPY];

/**
 * The words the SYSTEM banner uses when the menu is the page that noticed.
 *
 * Kept beside the watcher rather than in Menu's dictionary because it is read
 * when the guest is not looking at the menu at all — and it deliberately
 * matches what worker/push/webpush.ts sends, so the two paths cannot say
 * different things about the same drink.
 */
const READY_ALERT = {
  ro: { title: "Comanda e gata!", body: "Te așteptăm la bar." },
  ru: { title: "Заказ готов!", body: "Ждём вас в баре." },
  en: { title: "Your order is ready!", body: "Come to the bar." },
} as const;

/**
 * Everything this phone remembers about one order, forgotten together.
 *
 * ONLY for an order the server says does not exist. A finished order is not
 * forgotten — it is history, and „Comenzile mele" is built out of exactly
 * these crumbs.
 */
function forgetOrder(id: string) {
  localStorage.removeItem(`prispa.order.${id}`);
  localStorage.removeItem(`prispa.backto.${id}`);
  localStorage.removeItem(`prispa.notify.${id}`);
  localStorage.removeItem(`prispa.notifyask.${id}`);
  localStorage.removeItem(`prispa.readyalert.${id}`);
  localStorage.removeItem(`prispa.readyseen.${id}`);
  localStorage.removeItem(`prispa.orderinfo.${id}`);
  localStorage.removeItem(`prispa.payurl.${id}`);
}

/** One line of „Comenzile mele". */
export type PastOrder = {
  id: string;
  code: string;
  status: string;
  created_at: number;
  /** Only when this phone still holds the session that proves the order is ours. */
  total_minor?: number;
  /**
   * Failed because the payment window closed, not because a bank refused.
   * „Comenzile mele" prints a different word for it — see Menu.tsx.
   */
  payment_expired?: boolean;
};

const TERMINAL = ["handed_over", "cancelled", "refunded"];

/**
 * A FINISHED ORDER NEVER NEEDS ASKING ABOUT AGAIN, so it is frozen here.
 *
 * Without this, „Comenzile mele" would re-fetch every order of the evening on
 * every menu load — the exact per-visit cost the old sweep was avoiding by
 * DELETING the history, which is no longer an option now that the history is a
 * feature. A terminal status cannot change (nothing transitions out of
 * handed_over, cancelled or refunded), so the answer is cacheable for good.
 */
function cachedOrder(id: string): PastOrder | null {
  try {
    const raw = localStorage.getItem(`prispa.orderinfo.${id}`);
    return raw ? (JSON.parse(raw) as PastOrder) : null;
  } catch {
    return null;
  }
}

function cacheOrder(o: PastOrder): void {
  try {
    localStorage.setItem(`prispa.orderinfo.${o.id}`, JSON.stringify(o));
  } catch {
    /* quota — it will simply be fetched again next time */
  }
}

export function App() {
  const [r] = useState(route);
  // The first tap or scroll anywhere in the app buys the right to make a
  // sound later. Without it the chime is silent for every guest who never
  // tapped „Anunță-mă când e gata" — see primeChimeOnFirstGesture.
  useEffect(primeChimeOnFirstGesture, []);

  /*
   * THE SERVICE WORKER IS REGISTERED FOR THE WHOLE APP, not inside the
   * subscribe path.
   *
   * `navigator.serviceWorker.register("/sw.js")` used to live only in the
   * notification opt-in, which meant a guest with permission already granted
   * — from an earlier order, or an earlier version of this UI — had NO
   * registration at all. On Android that is fatal to the in-page banner too:
   * Chrome refuses `new Notification()` from a page and demands the
   * registration, so ready-alert.ts's `getRegistration()` came back undefined
   * and the constructor fallback threw. The moment that matters most produced
   * nothing.
   *
   * It caches nothing (public/sw.js has no fetch handler), so registering it
   * early costs one request and cannot affect what the menu serves.
   */
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* http:// preview, private mode, or a browser that refuses — every
         other tier still works without it */
    });
  }, []);
  /*
   * A WHITE-LABELLED DOMAIN WITH NO PATH.
   *
   * `meniu.pensiunea.md` on its own is what somebody types after seeing it on
   * a sign, a card or a Facebook post. Without this it rendered the guest shell
   * with no slug and said "we can't find this event's menu" — a poor first
   * impression of a domain the guesthouse has just had printed.
   *
   * /api/site answers "whose house is this hostname, and what is on tonight",
   * and we replace the URL rather than push it, so Back leaves the site instead
   * of bouncing between the bare domain and the menu.
   */
  const [siteLookup, setSiteLookup] = useState<"idle" | "looking" | "nothing">(
    r.kind === "menu" && !r.slug ? "looking" : "idle"
  );

  useEffect(() => {
    if (siteLookup !== "looking") return;
    let alive = true;
    fetch("/api/site")
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { event?: { slug: string } | null } | null) => {
        if (!alive) return;
        if (body?.event?.slug) location.replace(`/m/${encodeURIComponent(body.event.slug)}`);
        else setSiteLookup("nothing");
      })
      .catch(() => alive && setSiteLookup("nothing"));
    return () => { alive = false; };
  }, [siteLookup]);
  // Romanian is the default, full stop. It is the source language and the
  // language of the house — a guest whose PHONE is in English should still be
  // met in Romanian and switch if they want to. Only an explicit choice sticks.
  const [lang, setLang] = useState<string>(() => localStorage.getItem("prispa.lang") ?? "ro");
  /*
   * THE CHECKOUT IS IN THE URL, so a refresh does not throw the guest out of it.
   *
   * It used to be a bare boolean, which meant the one screen where a guest is
   * typing — name, tip, email, payment method — was also the one screen that
   * could not survive a reload. Refresh, an accidental Back, iOS discarding a
   * backgrounded tab: all of them landed on the menu, basket intact, form
   * empty. On a village connection, mid-payment, that is where an order is
   * abandoned.
   *
   * A HASH rather than a path: it needs no route in the Worker and no new
   * entry in `assets.run_worker_first` (D-23), and it costs nothing on the
   * server. It also gives the sheet the behaviour a phone already teaches —
   * BACK CLOSES IT instead of leaving the site, because opening it pushed a
   * history entry.
   *
   * The form's own contents survive separately, in the draft the checkout
   * writes (src/guest/Checkout.tsx). This only decides that the screen is open.
   */
  const [cartOpen, setCartOpen] = useState(() => location.hash === "#checkout");

  useEffect(() => {
    const sync = () => setCartOpen(location.hash === "#checkout");
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  const openCheckout = () => {
    // Assigning the hash pushes a history entry; `hashchange` opens the sheet.
    location.hash = "checkout";
  };
  const closeCheckout = () => {
    /*
     * replaceState, NOT history.back(). Back would be right for a guest who
     * opened the sheet from the menu — but not for one who arrived here by
     * RELOADING on #checkout, where the previous entry belongs to whatever
     * they were looking at before this site, and „Închide" would walk them off
     * it. Replacing the entry closes the sheet and leaves Back pointing where
     * it did before, in both cases.
     */
    history.replaceState(null, "", location.pathname + location.search);
    setCartOpen(false);
  };

  /*
   * The house's rules, lifted out of the menu snapshot.
   *
   * The checkout has to obey them and has no snapshot of its own; fetching a
   * second copy would be a second request on a connection this app is built to
   * spare. Null until the menu has loaded, which is also the only time the
   * basket button can be reached.
   */
  const [rules, setRules] = useState<HouseRules | null>(null);
  /*
   * WHAT THE HOUSE CALLS THE PLACE THE GUEST IS SITTING IN.
   *
   * Lifted out of the snapshot alongside the rules, and for the same reason:
   * the checkout needs it and has no snapshot of its own. Null until the menu
   * has loaded, and null for a QR carrying no zone at all.
   */
  const [zoneLabel, setZoneLabel] = useState<string | null>(null);

  const barMode = r.kind === "menu" && r.bar;
  /*
   * WHO IS BEHIND THE COUNTER — undefined until asked, null when nobody is.
   *
   * Asked ONCE, and only in bar mode: on the ordinary guest path this request
   * would be a round trip that always answers 401, on the connection this app
   * spends its whole budget protecting.
   */
  const [barStaff, setBarStaff] = useState<BarStaff | null | undefined>(
    barMode ? undefined : null
  );
  /** The order just typed, held on screen until „Comandă nouă" clears it. */
  const [barPlaced, setBarPlaced] = useState<{ code: string; total: number } | null>(null);

  useEffect(() => {
    if (!barMode) return;
    let alive = true;
    fetch("/api/auth/me")
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { name?: string; capabilities?: string[] } | null) => {
        if (!alive) return;
        setBarStaff(
          body
            ? {
                name: body.name ?? "",
                // The same capability worker/api/orders.ts demands. Read here
                // so the screen can say „acest cont nu poate" before a basket
                // is built, rather than after — but never INSTEAD of there.
                canOrder: (body.capabilities ?? []).includes("order.take_payment"),
              }
            : null
        );
      })
      .catch(() => alive && setBarStaff(null));
    return () => { alive = false; };
  }, [barMode]);

  const slug = r.kind === "menu" ? (r.slug ?? "") : "";
  /*
   * THE COUNTER'S BASKET IS NOT THE GUEST'S BASKET, even on the same phone.
   *
   * `useCart` keys localStorage on whatever string it is handed, and a waiter
   * who had earlier ordered a coffee at this same evening from this same phone
   * would otherwise open mod bar onto their own half-finished basket — and,
   * worse, onto its idempotency key, which the server would dedup the guest's
   * order against. The suffix never reaches the server: `eventSlug` is passed
   * to the checkout separately and is what the order is actually placed
   * against.
   */
  const cart = useCart((slug || "none") + (barMode ? "@bar" : ""));

  /*
   * AN EMPTIED BASKET CLOSES THE CHECKOUT FOR REAL, hash and all.
   *
   * The render guard on <Checkout> below already sent the guest back to the
   * menu when the last line left the order — but only by unmounting:
   * `#checkout` stayed armed, so the NEXT „Adaugă", minutes later and
   * mid-browse, teleported them straight into the order summary they thought
   * they had left. Deleting everything is the guest saying "not this order";
   * the sheet should need to be OPENED again, by them, the same way it was
   * the first time.
   */
  useEffect(() => {
    if (cartOpen && cart.count === 0) closeCheckout();
  }, [cartOpen, cart.count]);

  /*
   * AN ORDER THAT IS STILL SOMEBODY'S DEBT, found again from this phone.
   *
   * Every placed order left two localStorage crumbs: `prispa.order.<id>` (the
   * session that proves it is ours) and `prispa.backto.<id>` (the menu URL it
   * came from). Scanning the second for THIS slug turns the phone's own
   * history into the answer to "does this guest already owe the house an
   * order" — no account, no server-side identity, exactly the trust model the
   * rest of the guest app runs on. A guest who clears their storage walks past
   * the gate, and that is accepted: this is a courtesy rail against confusion
   * and stacked unpaid orders, not a security boundary — the bar still holds
   * the board, and cash still changes hands in person.
   *
   * Unpaid means awaiting_payment or unpaid_cash_pending: states where the
   * house is still owed something and the bottles are still held. paid /
   * preparing / ready pass freely — a second round while the first is on the
   * bar is the product working.
   *
   * 'failed' USED TO BE ON THIS LIST AND IT WAS BACKWARDS. A failed order holds
   * no stock (the room handed it back when the attempt died) and owes the house
   * nothing — there is no debt to collect and nothing to confuse the bar. All
   * the gate did there was stand between a guest whose card had just been
   * declined, or whose payment window had just lapsed, and the ability to buy a
   * drink. That guest is the LAST person to block: they have already decided to
   * spend money and been prevented once.
   *
   * The order is not hidden from them — it is one tap away in „Comenzile mele",
   * where the retry and the cancel button both live.
   *
   * The crumbs are NEVER swept for a finished order any more; they are the
   * index „Comenzile mele" is built from. What keeps the cost flat is that a
   * terminal order is frozen into `prispa.orderinfo.<id>` and never asked
   * about again — see cachedOrder above.
   */
  const [outstanding, setOutstanding] = useState<{ id: string; status: string } | null>(null);

  /**
   * Every order this phone has placed at this event, newest first — the list
   * „Comenzile mele" opens. Built from the same pass that hunts for an unpaid
   * one, so the history costs no extra request.
   */
  const [myOrders, setMyOrders] = useState<PastOrder[]>([]);

  /*
   * „COMANDA TA E GATA" — ON THE MENU, WHICH IS WHERE THE GUEST ACTUALLY IS.
   *
   * The order page announces the moment loudly, and Web Push reaches a closed
   * tab on Android — but between them sat the commonest case of all and it was
   * silent: the guest orders, taps „Înapoi la meniu", and reads about the
   * house while the bar makes their drink. That page knew nothing about any
   * order, so „E gata" arrived as nothing whatsoever.
   *
   * So the same crumbs that find an unpaid order also give us the orders worth
   * WATCHING, and this polls them. Everything the order page does, this does:
   * sound, vibration, a system banner when the phone is away — plus the thing
   * only a visible page can do, which is a pop-up the guest cannot miss
   * (rendered by Menu, dismissible, carrying the pickup code).
   *
   * „Am înțeles" is FINAL and survives a reload (readyDismissed in
   * ready-alert.ts). The code is not lost by dismissing it — „Comenzile mele"
   * in the header holds every order of the evening.
   */
  const [readyOrder, setReadyOrder] = useState<{ id: string; code: string } | null>(null);
  /** Order ids still worth polling, and the status each was last seen in. */
  const watchRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (r.kind !== "menu" || !slug) return;
    /*
     * NOT AT THE COUNTER. This whole apparatus — the unpaid gate, „Comenzile
     * mele", the ready chime — is about ONE guest's own orders on their own
     * phone, and mod bar places orders for other people. Left running, the
     * tablet would poll every order it had ever typed, chime for each one as
     * it came up, and refuse the next customer because the last one has not
     * paid yet. `onPlaced` writes no crumbs in bar mode, so there is nothing
     * here to find either; this is the belt.
     */
    if (barMode) return;
    let alive = true;

    const runScan = async () => {
      const prefix = `/m/${encodeURIComponent(slug)}`;
      const ids: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k?.startsWith("prispa.backto.")) continue;
        const v = localStorage.getItem(k) ?? "";
        if (v === prefix || v.startsWith(`${prefix}/`)) ids.push(k.slice("prispa.backto.".length));
      }

      const UNPAID = ["awaiting_payment", "unpaid_cash_pending"];
      let owed: { id: string; status: string } | null = null;
      const history: PastOrder[] = [];
      // ALL matching crumbs, not a window of them: localStorage.key() order is
      // unspecified, so any cap could drop exactly the unpaid order it exists
      // to find — or an order the guest is looking for in their history.
      for (const id of ids) {
        if (!alive) return;
        try {
          const seen = await look(id);
          if (seen === null) continue;
          history.push(seen);
          if (TERMINAL.includes(seen.status)) {
            // Finished for good: freeze it and never ask again. The crumb
            // stays, because it is this order's entry in „Comenzile mele".
            cacheOrder(seen);
            watchRef.current.delete(id);
            continue;
          }
          // Anything still alive is worth a poll — the bar can mark an order
          // ready from 'paid', 'preparing' AND 'unpaid_cash_pending'
          // (worker/api/bar.ts), so nothing non-terminal is excluded here.
          watchRef.current.set(id, seen.status);
          // The FIRST unpaid one gates the basket, but the loop keeps going:
          // returning early would leave later orders unwatched and missing
          // from the history, so an unpaid order could hide a ready one.
          if (!owed && UNPAID.includes(seen.status)) owed = { id, status: seen.status };
        } catch {
          /* offline — no gate; the checkout's own refusal is the backstop */
        }
      }
      if (!alive) return;
      // Null when nothing is owed: also how a STALE banner clears after a
      // bfcache restore.
      setOutstanding(owed);
      setMyOrders(history.sort((a, b) => b.created_at - a.created_at));
    };

    /**
     * Read one order and react to it. Returns null when the fetch was no use.
     *
     * The ready detection lives here rather than in the caller so that BOTH
     * the initial scan and the poll below announce identically — a guest who
     * opens the menu at the exact moment the drink is finished deserves the
     * same pop-up as one who was already sitting there.
     */
    async function look(id: string): Promise<PastOrder | null> {
      // A finished order cannot change, so the frozen copy answers without a
      // request. This is what keeps „Comenzile mele" from costing one fetch
      // per order of the evening on every single menu load.
      const frozen = cachedOrder(id);
      if (frozen) return frozen;

      const session = localStorage.getItem(`prispa.order.${id}`);
      const res = await fetch(`/api/orders/${id}${session ? `?s=${encodeURIComponent(session)}` : ""}`);
      if (res.status === 404) {
        // The server says it never existed. Only then is anything destroyed.
        forgetOrder(id);
        watchRef.current.delete(id);
        return null;
      }
      if (!res.ok) return null;
      const body = (await res.json()) as {
        order?: {
          status?: string; pickup_code?: string; created_at?: number;
          total_minor?: number; payment_expired?: boolean;
        };
      };
      const status = body.order?.status ?? "";
      const code = body.order?.pickup_code ?? "";
      if (!status) return null;

      /*
       * THE DEAD LINK GOES THE MOMENT THIS PHONE LEARNS IT IS DEAD.
       *
       * OrderStatus.tsx clears `prispa.payurl.<id>` on its own poll, but only
       * for the order it is looking at — and the commonest way to abandon a
       * payment is to close that page and go back to the menu. This scan is
       * then the only thing still asking about the order, so it has to keep the
       * same promise: a pay URL is meaningful in 'awaiting_payment' and nowhere
       * else. Past that it points at maib's „Link no longer accepting
       * payments", which is a worse answer than no button at all.
       */
      if (status !== "awaiting_payment") {
        localStorage.removeItem(`prispa.payurl.${id}`);
      }
      const seen: PastOrder = {
        id,
        code,
        status,
        created_at: body.order?.created_at ?? 0,
        // Absent from the limited response, which is correct: a phone that has
        // lost its session may know an order is ready and must not know its total.
        total_minor: body.order?.total_minor,
        payment_expired: body.order?.payment_expired,
      };

      if (status === "ready" && alive) {
        // The NOISE is once per order (alertAlreadyFired); the POP-UP is once
        // per order too, now that „Am înțeles" is remembered across reloads.
        if (!alertAlreadyFired(id)) {
          markAlertFired(id);
          announceReady({
            orderId: id,
            pickupCode: code,
            title: READY_ALERT[(lang in READY_ALERT ? lang : "ro") as keyof typeof READY_ALERT].title,
            body: `${code} · ${READY_ALERT[(lang in READY_ALERT ? lang : "ro") as keyof typeof READY_ALERT].body}`,
          });
        }
        if (!readyDismissed(id)) setReadyOrder({ id, code });
      }
      return seen;
    }

    void runScan();

    /*
     * THE WATCH. Fifteen seconds, and only for orders that can still change.
     *
     * Slower than the order page's five, because this is a background concern
     * on a page about something else, and the guest who wants the countdown is
     * on /o/<id> where it is faster.
     *
     * WHEN THE PHONE IS AWAY it keeps polling ONLY if the guest granted
     * notification permission — that permission IS the request to be told, and
     * a system banner is the only thing that can reach them there. Without it
     * the poll sleeps like the menu's availability poll does, because a page
     * nobody can see cannot show a pop-up and would only spend battery.
     */
    const tick = async () => {
      if (document.hidden && (!("Notification" in window) || Notification.permission !== "granted")) return;
      for (const id of [...watchRef.current.keys()]) {
        if (!alive) return;
        try {
          const seen = await look(id);
          if (seen === null) continue;
          if (TERMINAL.includes(seen.status)) {
            cacheOrder(seen);
            watchRef.current.delete(id);
          } else {
            watchRef.current.set(id, seen.status);
          }
          // Keep the history list in step, so „Comenzile mele" opened after a
          // status change shows the new word rather than the one from load.
          if (alive) {
            setMyOrders((prev) =>
              prev.some((o) => o.id === id)
                ? prev.map((o) => (o.id === id ? seen : o))
                : [seen, ...prev].sort((a, b) => b.created_at - a.created_at)
            );
          }
        } catch {
          /* offline — try again on the next tick */
        }
      }
    };
    const timer = setInterval(() => void tick(), 15_000);
    const onVisible = () => { if (!document.hidden) void tick(); };
    document.addEventListener("visibilitychange", onVisible);

    /*
     * BACK FROM THE ORDER PAGE, VIA THE BFCACHE. Safari and Chrome restore
     * this page frozen exactly as it was — banner up, gate armed — after the
     * guest has just paid or cancelled the order over on /o/<id>. `persisted`
     * is true only for those restores, so a fresh scan runs and the banner
     * tells the truth again without a reload.
     */
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void runScan();
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
    // `lang` is read for the notification's words only; re-running the scan on
    // a language switch is harmless (the alert is once-per-order regardless).
  }, [r.kind, slug, lang, barMode]);

  function changeLang(next: string) {
    localStorage.setItem("prispa.lang", next);
    setLang(next);
  }

  if (r.kind === "order") return <OrderStatus orderId={r.id} lang={lang} />;

  /*
   * THE SELLING PAGE, and it takes nothing from the apparatus above.
   *
   * No cart, no unpaid-order gate, no ready-alert watch: every one of those is
   * about ONE evening's drinks on the phone of somebody sitting in the house.
   * A guest choosing a weekend in September has no order to owe and no chime to
   * wait for, and threading them through would only give this page ways to be
   * wrong. The language, which IS shared, is passed down.
   */
  if (r.kind === "rent") return <Rent slug={r.slug} lang={lang} setLang={changeLang} />;

  if (siteLookup === "looking") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-linen px-6">
        <p className="text-ink-faint">…</p>
      </div>
    );
  }

  /*
   * NOBODY IS SIGNED IN ON THIS DEVICE — so mod bar says so, and stops.
   *
   * Rendering the menu anyway would be the cruellest version: a full basket
   * built at the counter with a customer waiting, refused only at the moment
   * somebody taps „Comandă". The cookie is the same one /staff sets, on this
   * same origin, so the fix is one link away.
   *
   * `undefined` is „not asked yet" and falls through to the menu — the answer
   * is one same-origin round trip and the basket bar is unreachable before it
   * lands anyway.
   */
  if (barMode && barStaff !== undefined && (barStaff === null || !barStaff.canOrder)) {
    const t = barCopy(lang);
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-linen px-6 text-center">
        <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)" }}>{t.who}</p>
        {barStaff === null ? (
          <>
            <a
              href="/staff"
              className="bg-clay-500 px-6 py-3 text-paper"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t.signIn}
            </a>
            <p className="text-ink-muted" style={{ fontSize: "var(--text-kicker)" }}>
              {t.signInHint}
            </p>
          </>
        ) : (
          <p className="text-ink-muted">{t.denied}</p>
        )}
      </div>
    );
  }

  return (
    <>
      {/*
        * THE ORDER JUST TYPED, HELD ON SCREEN UNTIL IT IS READ ALOUD.
        *
        * A counter order does NOT navigate to /o/<id> the way a guest's does.
        * Two reasons, and both are about the next customer: the staff need the
        * pickup code and the amount for the person standing in front of them
        * right now, and then they need to be back on the menu — not on an
        * order page belonging to somebody else, polling it and chiming when it
        * is ready.
        */}
      {barPlaced && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-olive-800 px-6 text-center text-linen">
          <p style={{ fontSize: "var(--text-kicker)" }}>{barCopy(lang).placed}</p>
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-kicker)" }}>
            {barCopy(lang).code}
          </p>
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-3xl)", lineHeight: 1 }}>
            {barPlaced.code}
          </p>
          <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)" }}>
            {barCopy(lang).collect}: {lei(barPlaced.total)}
          </p>
          <p className="max-w-xs" style={{ fontSize: "var(--text-kicker)" }}>
            {barCopy(lang).settle}
          </p>
          <button
            type="button"
            onClick={() => setBarPlaced(null)}
            className="mt-2 bg-paper px-6 py-3 text-ink"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {barCopy(lang).next}
          </button>
        </div>
      )}
      <Menu
        slug={slug}
        zone={r.zone}
        bar={barMode ? (barStaff?.name ?? "") : null}
        lang={lang}
        setLang={changeLang}
        cartCount={cart.count}
        cartSubtotal={cart.subtotal}
        qtyOf={cart.qtyOf}
        onAdd={cart.add}
        onSetQty={cart.setQty}
        onNames={cart.relabel}
        /*
         * PAY OR CANCEL THE OLD ORDER BEFORE PLACING THE NEXT ONE — but the
         * GATE LIVES IN THE MENU now, beside the banner it points at. This
         * used to intercept the tap here and navigate to /o/<id> without a
         * word; the menu's bar answers instead, with the same
         * sentence-scroll-flag refusal the checkout uses, and passes through
         * to here only when nothing is owed.
         */
        onOpenCart={openCheckout}
        onRules={setRules}
        onZoneLabel={setZoneLabel}
        unpaidOrderId={outstanding?.id ?? null}
        readyOrder={readyOrder}
        onDismissReady={() => {
          // Remembered, so a reload does not throw it at them again.
          if (readyOrder) markReadyDismissed(readyOrder.id);
          setReadyOrder(null);
        }}
        myOrders={myOrders}
      />

      {/*
        * `cart.count > 0` GUARDS THE RESTORE, not the opening.
        *
        * #checkout can outlive the basket it belonged to — the guest places
        * the order (the cart is cleared, then we navigate away), taps Back,
        * and the restored page has the hash and nothing to sell. A checkout
        * with no lines can only fail: the server refuses `items: []` with a
        * validation error, on a screen that offers no way out but Back. The
        * menu is the honest thing to show — and the effect beside
        * `closeCheckout` above then disarms the hash too, so the next
        * „Adaugă" adds to the basket instead of reopening a summary nobody
        * asked for.
        */}
      {cartOpen && cart.count > 0 && (
        <Checkout
          rules={rules}
          eventSlug={slug}
          zone={r.zone}
          zoneLabel={zoneLabel}
          lines={cart.lines}
          subtotal={cart.subtotal}
          idempotencyKey={cart.idempotencyKey}
          lang={lang}
          bar={barMode}
          onQty={cart.setQty}
          onClose={closeCheckout}
          /*
           * A COUNTER ORDER ENDS HERE AND NOWHERE ELSE.
           *
           * None of the four localStorage crumbs below are written: the
           * session that proves an order is „mine", the pay URL, the way back
           * to the table, the entry in „Comenzile mele". Every one of them
           * belongs to the person who is buying, and at the counter that is
           * not the person holding the device. Writing them would put a
           * stranger's order into the staff phone's history and set it polling
           * and chiming for a drink somebody else ordered.
           */
          onBarPlaced={(code, total) => {
            cart.clear();
            closeCheckout();
            setBarPlaced({ code, total });
          }}
          onPlaced={(orderId, payUrl, method) => {
            // Stash the pay URL so the order screen can offer "pay now" if the
            // guest closes the bank page and comes back.
            /*
             * localStorage, NOT sessionStorage — „Plătește acum" has to
             * survive the journey it exists for.
             *
             * sessionStorage is scoped to ONE TAB's lifetime, and the pay
             * journey routinely leaves it: MIA opens the banking app, a bank
             * page can return into a fresh tab, iOS evicts a backgrounded tab
             * under memory pressure, and a guest who closes the page and
             * reopens /o/<id> from history gets a brand new session. Every one
             * of those came back to an order that still says „Așteptăm plata"
             * with no way to pay it — the exact dead end this stash was
             * introduced to prevent.
             *
             * The key dies with the order's `awaiting_payment` state, which
             * OrderStatus.tsx enforces on every poll, so nothing here outlives
             * its usefulness.
             */
            if (payUrl) localStorage.setItem(`prispa.payurl.${orderId}`, payUrl);
            /*
             * KEEP THE SESSION THAT PROVES THIS ORDER IS MINE.
             *
             * `GET /api/orders/:id` returns the totals only to whoever presents
             * it — the id alone is not a credential for money, because the bar
             * tablet reads every order id off its board all evening.
             *
             * localStorage rather than sessionStorage: the guest closes the
             * bank page, comes back in a new tab, or reopens the phone an hour
             * later to check whether it is ready. `cart.clear()` on the next
             * line mints a NEW key, so it has to be copied out first.
             */
            const session = cart.idempotencyKey.slice(0, 40);
            localStorage.setItem(`prispa.order.${orderId}`, session);
            /*
             * WHERE "ÎNAPOI LA MENIU" GOES. It went to "/", which is not a
             * menu: the guest menu lives at /m/<slug>, "/" resolves no slug,
             * and the guest who had just paid landed on "Nu găsim meniul
             * acestui eveniment." The table they are sitting at has the right
             * URL printed on a card, and the app sent them somewhere else.
             *
             * Recorded at the moment the order is placed, because that is the
             * only moment both facts — the order and the table it came from —
             * are in the same place.
             */
            localStorage.setItem(
              `prispa.backto.${orderId}`,
              `/m/${encodeURIComponent(slug)}${r.zone ? `/${encodeURIComponent(r.zone)}` : ""}`
            );
            cart.clear();
            /*
             * THE SESSION RIDES THE URL TO THE ORDER PAGE. localStorage above
             * already remembers it for THIS browser; the query param is for
             * the one storage cannot reach — an iPhone guest who installs the
             * app from the order page. iOS gives an installed PWA a SEPARATE
             * storage partition, so the URL is the only thing „Adaugă pe
             * ecranul principal" carries across. OrderStatus strips it
             * immediately on any platform that does not need the carry.
             */
            /*
             * MIA KEEPS THE GUEST HERE; A CARD PAGE MAY TAKE THEM.
             *
             * A card checkout is a round trip — maib redirects the payer back
             * to `/o/<id>` when it is done, so leaving is safe and lands them
             * where they started.
             *
             * MIA is not a round trip and cannot be made into one. Its URL is
             * the QR's own PAYLOAD (maib's MIA API has no okUrl, failUrl or
             * returnUrl at all, because a QR is scanned by a banking app and a
             * banking app has nowhere to send anybody back to). Navigating
             * there stranded the guest on bnm.md: they paid, and then sat on a
             * page that has never heard of us, with no way back to their order.
             *
             * So MIA lands on the order page instead, which is the one screen
             * that already polls, already holds the mask, and already receives
             * the callback. The pay URL was stashed above, so the button to
             * open the bank is waiting there — and it opens in a new tab, so
             * this page survives the journey.
             */
            location.href =
              payUrl && method !== "mia"
                ? payUrl
                : `/o/${orderId}?s=${encodeURIComponent(session)}`;
          }}
        />
      )}
    </>
  );
}
